import React from 'react'

/**
 * TransferAlertBanner — port of Scale's TransferAlertBanner.
 * Renders an info alert at the top of the case detail page when the case
 * was transferred *to* the current mediator. Includes the from-mediator
 * name, transfer date, and Zoom alternative-host credentials so the case
 * manager (acting on the new mediator's behalf) can log in correctly.
 */
export default function TransferAlertBanner({ transferHistory, caseData, mediatorData }) {
    if (!transferHistory) return null

    const fromMediator = transferHistory.from_mediator
    const fromMediatorName = fromMediator
        ? `${fromMediator.first_name || ''} ${fromMediator.last_name || ''}`.trim() || 'Unknown Mediator'
        : 'Unknown Mediator'

    const dateRaw = transferHistory.transferred_at || transferHistory.created_at
    const transferDate = dateRaw
        ? new Date(dateRaw).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        : ''

    const altHostEmail = fromMediator?.mediator_settings?.zoom_alternate_host?.email
    const altHostPassword = fromMediator?.mediator_settings?.zoom_alternate_host?.password

    return (
        <div className="mb-4 mt-2 rounded-lg ring-1 ring-blue-200 bg-blue-50 border border-blue-100 px-4 py-3">
            <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                    <h4 className="text-sm font-semibold text-blue-900">Case Transfer Notification</h4>
                    <div className="space-y-3 mt-2">
                        <div className="text-sm text-gray-800 space-y-1 leading-relaxed">
                            <p>
                                This case has been transferred from{' '}
                                <span className="font-semibold text-blue-700">{fromMediatorName}</span>{' '}
                                to you on{' '}
                                <span className="font-semibold text-blue-700">{transferDate}</span>.
                            </p>
                            <p>
                                The Zoom meeting details have been updated. Please review the quick summary below so you are
                                ready to log in as the alternative host on the day of the mediation.
                            </p>
                        </div>

                        <div className="bg-white border border-blue-100 rounded-md p-3 space-y-2">
                            <p className="text-xs font-semibold text-blue-900 uppercase tracking-wide">Zoom Access Summary</p>
                            <ul className="list-disc pl-5 text-sm text-gray-800 space-y-1">
                                <li>
                                    <span className="font-semibold">Alternative-host email:</span>{' '}
                                    {altHostEmail || 'Not provided'}
                                </li>
                                <li>
                                    <span className="font-semibold">Zoom password:</span>{' '}
                                    <code className="px-1 py-0.5 bg-blue-50 rounded border border-blue-100">
                                        {altHostPassword || 'Not provided'}
                                    </code>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
